import { ModelSnapshotType, applySnapshot, getSnapshot, types } from "mobx-state-tree";

import * as Sentry from "@sentry/react";
import api from "api";
import { UserObject, UserRoleTypes } from "api/endpoints/users";
import config from "config";
import Errors from "./errors";
import Notifications from "./notifications";
import Tokens from "./tokens";

export enum LoginStatus {
  LoggedOut = "logged-out",
  LoggingIn = "logging-in",
  LoggedIn = "logged-in",
  LoggingOut = "logging-out",
}

let initialSnapshot: ModelSnapshotType<any>;

export const RootStore = types
  .model("RootStore", {
    loginStatus: types.optional(
      types.enumeration<LoginStatus>("loginStatus", [
        LoginStatus.LoggedOut,
        LoginStatus.LoggingIn,
        LoginStatus.LoggedIn,
        LoginStatus.LoggingOut,
      ]),
      LoginStatus.LoggingIn
    ),
    userRole: types.maybe(
      types.enumeration<UserRoleTypes>("userRole", [UserRoleTypes.User, UserRoleTypes.Admin])
    ),
    tokens: Tokens,
    errors: Errors,
    notifications: Notifications,
  })
  .views((self) => ({
    get isLoggedIn() {
      return self.loginStatus === LoginStatus.LoggedIn;
    },
    get isLoggedOut() {
      return self.loginStatus === LoginStatus.LoggedOut;
    },
    get isLoggingIn() {
      return self.loginStatus === LoginStatus.LoggingIn;
    },
    get isLoggingOut() {
      return self.loginStatus === LoginStatus.LoggingOut;
    },
    get isUser() {
      return self.userRole === UserRoleTypes.User;
    },
    get isSuperAdmin() {
      return self.userRole === UserRoleTypes.Admin;
    },
  }))
  .actions((self) => ({
    afterCreate: () => {
      initialSnapshot = getSnapshot(self);
    },
    resetStore: () => {
      applySnapshot(self, initialSnapshot);
    },
    setLoginStatus(status: LoginStatus) {
      self.loginStatus = status;
    },
    setUserRole(role: UserRoleTypes) {
      self.userRole = role;
    },
  }))
  .actions((self) => ({
    init: async () => {
      self.setLoginStatus(LoginStatus.LoggingIn);
      self.tokens.restoreTokens();
      if (self.tokens.hasToken) {
        self.setLoginStatus(LoginStatus.LoggedIn);
      } else {
        self.setLoginStatus(LoginStatus.LoggedOut);
      }
    },
    currentUser: async (user: UserObject) => {
      Sentry.setUser({
        username: user.id,
      });

      self.setUserRole(api.users.roleForUser(user).role);
    },
    login: async (data: { username: string; password: string }) => {
      self.setLoginStatus(LoginStatus.LoggingIn);
      try {
        const response = await api.auth
          .getToken({
            client_id: config.API_CLIENT_ID,
            client_secret: config.API_CLIENT_SECRET,
            grant_type: "password",
            ...data,
          })
          .fetch();

        self.tokens?.setAccessToken(response.access_token);
        self.tokens?.setRefreshToken(response.refresh_token);

        const user = await api.users.getCurrentUser().fetch();

        Sentry.setUser({
          username: user.id,
        });

        self.setUserRole(api.users.roleForUser(user).role);

        self.setLoginStatus(LoginStatus.LoggedIn);
        document.body.classList.add("has-site-nav");
        return true;
      } catch (error) {
        console.error(error);
        self.setLoginStatus(LoginStatus.LoggedOut);
        return false;
      }
    },
    reset: async (data: { username: string; token: string }) => {
      self.setLoginStatus(LoginStatus.LoggingIn);
      try {
        const response = await api.auth
          .confirmResetPassword({
            email: data.username,
            client_id: config.API_CLIENT_ID,
            client_secret: config.API_CLIENT_SECRET,
            grant_type: "reset_password_grant",
            ...data,
          })
          .fetch();
        self.tokens?.setAccessToken(response.access_token);
        self.tokens?.setRefreshToken(response.refresh_token);

        const user = await api.users.getCurrentUser().fetch();

        Sentry.setUser({
          username: user.id,
        });

        self.setUserRole(api.users.roleForUser(user).role);

        self.setLoginStatus(LoginStatus.LoggedIn);
        document.body.classList.add("has-site-nav");
        return true;
      } catch (error) {
        console.error(error);
        self.setLoginStatus(LoginStatus.LoggedOut);
        throw Error("Password reset request failed. Please try again.");
      }
    },
    loginWithTokens: (data: { access_token: string; refresh_token: string }) => {
      self.setLoginStatus(LoginStatus.LoggingIn);
      self.tokens?.setAccessToken(data.access_token);
      self.tokens?.setRefreshToken(data.refresh_token);
      self.setLoginStatus(LoginStatus.LoggedIn);
      document.body.classList.add("has-site-nav");
      return true;
    },
    logout: async () => {
      self.tokens?.setAccessToken(null);
      self.tokens?.setRefreshToken(null);

      self.resetStore();
      self.setLoginStatus(LoginStatus.LoggedOut);

      Sentry.setUser(null);
      document.body.classList.remove("has-site-nav");

      window.location.reload();
    },
  }));

export default RootStore;
